import React from "react";
import { Entry } from ".";
import styled from "styled-components";
import { Checkbox, DatePicker, Icon, TimePicker, Option, Spacer, MultiSelect, Input } from "src/components";
import { theme } from "src/config";
import { UserSelect } from "src/components/userSelect";
import { Item, User } from "src/models";
import { ItemSelect } from "src/components/itemSelect";
import { IItem, LoadingStatus } from "src/types";
import { ProgressSlider } from "src/components";

export const MenuEntry = (props: Entry): JSX.Element => {
  const {type, hidden, itemType, validRange, placeholder, title,  iconId, iconColor,  value,  onChange, onClick, disabled, options, onAdd, onRemove, isLoading, onLoad, showLoadMore, onOpenSelect, step } = props;
  
  const key = title.replace(' ', '');

  switch(type) {
    case "progressSlider": 
      return (
        <ProgressSlider
        label    = {title}
        value    = {value}
        hidden   = {hidden}
        onChange = {onChange}
        step     = {step as number}/>
      )
    case "select": 
      return (
        <SelectContainer disabled={disabled}>
          <MultiSelect 
            singleSelect
            label    = {title}
            options  = {options}
            selected = {Boolean(value) ? 
              (Array.isArray(value) ?
              value.map(i => ({
                value: i,
                label: (i as Item<IItem>).title
              })) : [{
                value: value,
                label: value
              }]) : []}
            onChange     = {(option: Option) => onChange(option?.value)}
            disabled     = {disabled}
          />
        </SelectContainer>
      )
    case "checkbox":
      return (
        <Container disabled={disabled}>
          <Checkbox
            checked  = {value as boolean}
            disabled = {disabled}
            label    = {title}
            onClick  = {onClick}
          />
        <Spacer size="xxxsmall"/>
        </Container>
      );
    case "date":
      return (
        <Container disabled={disabled}>
          <Spacer size="xxxsmall"/>
          <DatePicker
            label         = {title}
            value         = {value as Date}
            onChange      = {onChange}
            
            disabled      = {disabled}
            calendarProps = {{
              editable:           true,
              selectable:         true,
              weekNumbers:        true,
              handleWindowResize: true,
              validRange:         validRange,
            }}
          />
        </Container>
      );
    case "time":
      return (
        <Container disabled={disabled}>
          <Spacer size="xxxsmall"/>
          <TimePicker
            label         = {title}
            value         = {value as Date || new Date()}
            onChange      = {onChange}
            disabled      = {disabled}
          />
        </Container>
      );
    case "items":
      return (
        <SelectContainer disabled={disabled}>
          <ItemSelect 
            label    = {title}
            options  = {options}
            itemType = {itemType}
            selected = {(Boolean(value) && Array.isArray(value)) ? value.map(i => ({
              value: i.id,
              label: (i as Item<IItem>).title
            })) : []}
            onAdd        = {(option: Option) => onAdd(option?.value)}
            onRemove     = {(option: Option) => onRemove(option?.value)}
            disabled     = {disabled}
            isLoading    = {isLoading !== LoadingStatus.Finished}
            onLoad       = {onLoad}
            showLoadMore = {showLoadMore}
            onOpenSelect = {onOpenSelect}
          />
        </SelectContainer>
      );
    case "users":
      return (
        <SelectContainer disabled={disabled}>
          <UserSelect 
            label    = {title}
            options  = {options}
            selected = {(Boolean(value) && Array.isArray(value)) ? value.map(u => ({
              value: u,
              label: (u as User).displayName
            })) : []}
            onAdd        = {(option: Option) => onAdd(option?.value)}
            onRemove     = {(option: Option) => onRemove(option?.value)}
            disabled     = {disabled}
            isLoading    = {isLoading !== LoadingStatus.Finished}
            onLoad       = {onLoad}
            showLoadMore = {showLoadMore}
          />
        </SelectContainer>
      );
    case "user":
      return (
        <SelectContainer disabled={disabled}>
          <UserSelect 
            label        = {title}
            disabled     = {disabled}
            isLoading    = {isLoading && isLoading !== LoadingStatus.Finished}
            onChange     = {(option: Option) => onChange(option?.value)}
            onLoad       = {onLoad}
            options      = {options}
            selected     = {Boolean(value) ? [{value: value, label: (value as User).displayName}] : []}
            showLoadMore = {showLoadMore}
            singleSelect
          />
        </SelectContainer>
      );
    case "action":
    default:
      return (
        <ActionContainer
          onClick  = {disabled ? undefined : onClick}
          disabled = {disabled}
        >
          <Icon
            size     = "default"
            label    = {title}
            iconId   = {iconId}
            color    = {iconColor}
            disabled = {disabled}
          />
        </ActionContainer>
      );
    case "number":
      return (
        <div key={key}>
          <Input
            key         = {key}
            type        = "number"
            min         = "-1"
            label       = {title}
            id          = "number"
            placeholder = {placeholder}
            value       = {value}
            onChange    = {onChange}
            icons       = {[
              {
                iconId: iconId,
              }
            ]}
          />
          <Spacer size="xsmall"/>
        </div>
      )
  }
};

const Container = styled.div<{ disabled: boolean }>`
  margin: 1rem 0rem;
  pointer-events: ${p => p.disabled ? "none" : "auto"};
`;

const SelectContainer = styled.div<{ disabled: boolean }>`
  margin:         1rem 0rem;
  text-transform: capitalize;
  pointer-events: ${p => p.disabled ? "none" : "auto"};
`;

const ActionContainer = styled(Container)<{ disabled: boolean }>`
  display:        flex;
  align-items:    center;
  transition:     padding-left 0.1s ease-in-out;
  :hover {
    background-color: ${(props) =>
      props.disabled ? undefined : theme.color.hover};
  }
  :active {
    padding-left: ${(props) => (props.disabled ? undefined : "0.4rem")};
  }
`;
